import {
  visitEntities,
  type BucketDocument,
  type BucketEntity,
  type BucketValueKind
} from '@oysterjs/smartfill-contracts';

/**
 * One in-flight edit. Existing properties keep their semantic key (renames
 * would be a different fact; the server would mint a new node anyway) and
 * edit display label, kind, and value. New properties derive their semantic
 * key from the display label.
 */
export interface PropertyDraft {
  entityId: string;
  /** Null while creating a new property under entityId. */
  propertyId: string | null;
  semanticKey: string;
  displayLabel: string;
  kind: BucketValueKind;
  value: string;
}

export interface SectionDraft {
  parentEntityId: string;
  label: string;
}

/** Turns a snake_case or dotted machine name into a human-readable label. */
export function humanizeName(name: string): string {
  const segment = name.split('.').filter(Boolean).at(-1) ?? name;
  const spaced = segment
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  if (!spaced) return 'Field';
  return spaced
    .split(/\s+/)
    .map((word, index) => {
      const uppercase = semanticAcronyms.get(word.toLowerCase());
      if (uppercase) return uppercase;
      const normalized = word.toLowerCase();
      return index === 0 ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : normalized;
    })
    .join(' ');
}

/** One property of a bucket document, flattened with its entity lineage. */
export interface FlatDocumentProperty {
  propertyId: string;
  semanticKey: string;
  displayLabel: string;
  value: string;
  /** Human path of owning entities, root first, e.g. "Acme LLC · Driver 1". */
  entityPath: string;
}

export function entityDisplayLabel(entity: BucketEntity, ordinal?: number): string {
  const base = entity.label || humanizeName(entity.role || entity.relation || entity.type);
  return ordinal === undefined ? base : `${base} ${ordinal}`;
}

/** Flattens every property of a document for pickers and counts. */
export function flattenDocumentProperties(document: BucketDocument): FlatDocumentProperty[] {
  const properties: FlatDocumentProperty[] = [];
  const walk = (entity: BucketEntity, path: string[]) => {
    for (const property of entity.properties ?? []) {
      properties.push({
        propertyId: property.id,
        semanticKey: property.semanticKey,
        displayLabel: property.displayLabel || humanizeName(property.semanticKey),
        value: property.value,
        entityPath: path.join(' · ')
      });
    }
    for (const { child, ordinal } of childrenWithOrdinals(entity)) {
      walk(child, [...path, entityDisplayLabel(child, ordinal)]);
    }
  };
  walk(document.root, [entityDisplayLabel(document.root)]);
  return properties;
}

/**
 * An entity's children paired with 1-based ordinals for repeated relations
 * ("Driver 1", "Driver 2"); a child whose relation is unrepeated carries no
 * ordinal. The tree view and the teach picker must agree on this rule, or
 * users would see different names for the same record.
 */
export function childrenWithOrdinals(
  entity: BucketEntity
): { child: BucketEntity; ordinal?: number }[] {
  const children = entity.children ?? [];
  const relationCounts = new Map<string, number>();
  for (const child of children) {
    relationCounts.set(child.relation ?? '', (relationCounts.get(child.relation ?? '') ?? 0) + 1);
  }
  const relationOrdinals = new Map<string, number>();
  return children.map((child) => {
    const relation = child.relation ?? '';
    const ordinal = (relationOrdinals.get(relation) ?? 0) + 1;
    relationOrdinals.set(relation, ordinal);
    return (relationCounts.get(relation) ?? 0) > 1 ? { child, ordinal } : { child };
  });
}

/** One set of repeated sibling records: every "drivers" child of one parent. */
export interface RepeatedList {
  /** Owning entity id plus relation; identifies the list across rows. */
  key: string;
  relation: string;
  entities: { entity: BucketEntity; ordinal?: number }[];
}

/**
 * The innermost repeated list a property belongs to, or null when the property
 * is unknown (a derived composite never appears in the document) or every
 * entity on its path is a singleton. The resolver refuses a repeated record's
 * property until the fill is scoped to one sibling; this names those siblings.
 */
export function repeatedListFor(document: BucketDocument, propertyId: string): RepeatedList | null {
  const path = entityPathToProperty(document.root, propertyId);
  if (!path) return null;
  for (let index = path.length - 1; index > 0; index -= 1) {
    const list = repeatedListUnder(path[index - 1], path[index].relation ?? '');
    if (list) return list;
  }
  return null;
}

/** Every repeated list in the document, root-first. */
export function repeatedListsIn(document: BucketDocument): RepeatedList[] {
  const lists: RepeatedList[] = [];
  const walk = (entity: BucketEntity) => {
    const seen = new Set<string>();
    for (const child of entity.children ?? []) {
      const relation = child.relation ?? '';
      if (seen.has(relation)) continue;
      seen.add(relation);
      const list = repeatedListUnder(entity, relation);
      if (list) lists.push(list);
    }
    for (const child of entity.children ?? []) walk(child);
  };
  walk(document.root);
  return lists;
}

function repeatedListUnder(parent: BucketEntity, relation: string): RepeatedList | null {
  const entities = childrenWithOrdinals(parent)
    .filter(({ child, ordinal }) => (child.relation ?? '') === relation && ordinal !== undefined)
    .map(({ child, ordinal }) => ({ entity: child, ordinal }));
  if (entities.length < 2) return null;
  return { key: `${parent.id}:${relation}`, relation, entities };
}

function entityPathToProperty(entity: BucketEntity, propertyId: string): BucketEntity[] | null {
  if ((entity.properties ?? []).some((property) => property.id === propertyId)) return [entity];
  for (const child of entity.children ?? []) {
    const path = entityPathToProperty(child, propertyId);
    if (path) return [entity, ...path];
  }
  return null;
}

export function countDocumentProperties(document: BucketDocument): number {
  let count = 0;
  visitEntities(document.root, (entity) => {
    count += entity.properties?.length ?? 0;
  });
  return count;
}

const semanticAcronyms = new Map([
  ['acord', 'ACORD'],
  ['coi', 'COI'],
  ['dba', 'DBA'],
  ['fein', 'FEIN'],
  ['id', 'ID'],
  ['zip', 'ZIP']
]);
